import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Search } from "lucide-react";
import { PLATFORM_META } from "@/lib/platforms";

function ResultCard({ platform, r, idx }) {
  const meta = PLATFORM_META[platform];
  const Icon = meta?.Icon;

  // For legacy discord records
  if (platform === "discord") {
    return (
      <div className="gc-card p-4" data-testid="checker-result-card">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            {Icon && <Icon size={14} />}
            <span className="font-mono text-xs text-[#A1A1AA]">RECORD #{idx + 1}</span>
          </div>
          <span className="gc-badge gc-badge-red">MATCH</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <Field label="Discord ID" value={r.discord_id} mono />
          <Field label="Email" value={r.email || "-"} mono />
          <Field label="IP" value={r.ip || "-"} mono />
          <Field label="Doğrulanmış" value={r.verified ? "Evet" : "Hayır"} />
          <Field label="Premium" value={r.premium ? "Evet" : "Hayır"} />
          <Field label="Discoin" value={String(r.discoin ?? 0)} />
        </div>
        {r.connections && (Array.isArray(r.connections) ? r.connections.length : Object.keys(r.connections || {}).length) > 0 && (
          <div className="mt-3">
            <div className="gc-label mb-1.5">BAĞLANTILAR</div>
            <pre className="text-[11px] bg-[#050505] border border-[#141414] p-3 overflow-x-auto font-mono text-[#EDEDED] max-h-56 rounded-lg">
{JSON.stringify(r.connections, null, 2)}
            </pre>
          </div>
        )}
        {r.server_ids?.length > 0 && (
          <div className="mt-3">
            <div className="gc-label mb-1.5">SUNUCULAR ({r.server_ids.length})</div>
            <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
              {r.server_ids.slice(0, 40).map((s, i) => (
                <span key={i} className="gc-badge">{String(s)}</span>
              ))}
              {r.server_ids.length > 40 && (
                <span className="gc-badge gc-badge-gold">+{r.server_ids.length - 40}</span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Generic platform records
  const data = r.data || {};
  const entries = Object.entries(data);
  return (
    <div className="gc-card p-4" data-testid="checker-result-card">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2" style={{ color: meta?.color }}>
          {Icon && <Icon size={14} />}
          <span className="font-mono text-xs">RECORD #{idx + 1}</span>
        </div>
        <span className="gc-badge gc-badge-red">MATCH</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <Field label="Primary Key" value={r.primary_key} mono />
        {entries.slice(0, 20).map(([k, v]) => (
          <Field key={k} label={k} value={fmt(v)} mono={typeof v !== "string" || v.length < 50} />
        ))}
      </div>
      {entries.length > 20 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-[#A1A1AA] font-mono">Tüm alanları göster ({entries.length})</summary>
          <pre className="mt-2 text-[11px] bg-[#050505] border border-[#141414] p-3 overflow-x-auto font-mono text-[#EDEDED] max-h-72 rounded-lg">
{JSON.stringify(data, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

function fmt(v) {
  if (v === null || v === undefined) return "-";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="gc-label">{label}</div>
      <div className={`text-[#EDEDED] mt-1 break-all ${mono ? "font-mono text-xs" : "text-sm"}`}>
        {value}
      </div>
    </div>
  );
}

export default function Checker() {
  const { platform = "discord" } = useParams();
  const meta = PLATFORM_META[platform];
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("id") || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null);

  const run = async (value) => {
    setError("");
    setResults(null);
    setBusy(true);
    try {
      const endpoint = `/checker/${platform}/query`;
      const { data } = await api.post(endpoint, { query: value.trim() });
      setResults(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    setResults(null);
    setError("");
    const preset = params.get("id");
    if (preset && !busy) {
      setQ(preset);
      run(preset);
    }
    // eslint-disable-next-line
  }, [platform]);

  const submit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    run(q);
  };

  if (!meta) {
    return <div className="text-[#A1A1AA]">Bilinmeyen platform.</div>;
  }

  const Icon = meta.Icon;

  return (
    <div className="max-w-none w-full">
      <div className="mb-5 flex items-center gap-3">
        <div
          className="w-10 h-10 flex items-center justify-center border border-[#1a1a1a] bg-[#0a0a0a] rounded-xl"
          style={{ color: meta.color }}
        >
          <Icon size={18} />
        </div>
        <div>
          <div className="gc-label">SORGU MODÜLÜ</div>
          <h1 className="font-heading text-2xl md:text-3xl font-black leading-none mt-1">
            {meta.name.replace(" Sorgu", "")} <span className="text-[#9D4EDD]">Sorgu</span>
          </h1>
        </div>
      </div>

      <form onSubmit={submit} className="gc-card p-4 mb-4" data-testid="checker-form">
        <label className="gc-label block mb-2">ARAMA</label>
        <div className="flex gap-2">
          <input
            data-testid="checker-input"
            className="gc-input flex-1"
            placeholder={meta.hint}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            required
          />
          <button
            data-testid="checker-submit"
            className="gc-btn-primary flex items-center gap-1.5"
            disabled={busy}
          >
            <Search size={13} /> {busy ? "TARANIYOR…" : "TARA"}
          </button>
        </div>
        {error && (
          <div
            data-testid="checker-error"
            className="mt-3 text-[#9D4EDD] text-xs font-mono border border-[#9D4EDD]/40 bg-[#9D4EDD]/5 p-2.5 rounded-lg"
          >
            {error}
          </div>
        )}
      </form>

      {results && (
        <div>
          <div className="mb-3 flex items-center gap-2">
            <span className="gc-badge gc-badge-red">SONUÇ</span>
            <span
              className="text-xs font-mono text-[#A1A1AA]"
              data-testid="checker-count"
            >
              {results.count} kayıt / {results.query}
            </span>
          </div>
          {results.count === 0 && (
            <div className="gc-card p-6 text-center text-sm text-[#A1A1AA]">
              Bu arama için sistemde eşleşen kayıt bulunamadı. Admin panelinden bu platforma
              ait veri yüklendiyse burada görünecek.
            </div>
          )}
          <div className="grid grid-cols-1 gap-3" data-testid="checker-results-list">
            {(results.results || []).map((r, i) => (
              <ResultCard platform={platform} r={r} idx={i} key={i} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
